import { useState, FormEvent } from 'react';

const contactInfo = [
  {
    icon: 'ri-map-pin-2-line',
    label: 'Address',
    lines: ['Velimira Zivojinovica Masuke 8', 'Velika Plana, 238623', 'Serbia'],
  },
  {
    icon: 'ri-mail-line',
    label: 'Email',
    lines: ['info@akiandoo.com'],
  },
  {
    icon: 'ri-phone-line',
    label: 'Phone',
    lines: ['+1 6067558259'],
  },
];

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch('https://readdy.ai/api/form/d7b4pu49quqsda8uesn0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-28 md:py-36 bg-[#0d1b2a]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#00d4aa] text-xs font-bold tracking-[4px] uppercase mb-4">Get In Touch</p>
          <h2 className="text-5xl md:text-6xl font-black text-white leading-tight">
            Contact <span className="italic font-light text-white/40">Us</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="flex flex-col gap-8">
            <p className="text-white/60 text-base leading-relaxed">
              Have questions about our products or want to learn more about AKIAN DOO?
              We&apos;d love to hear from you. Reach out through any of the channels below.
            </p>

            <div className="flex flex-col gap-6">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex gap-4 items-start">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00d4aa]/10 shrink-0 mt-0.5">
                    <i className={`${item.icon} text-[#00d4aa] text-lg`} />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs font-bold tracking-[2px] uppercase mb-1">
                      {item.label}
                    </p>
                    {item.lines.map((line) => (
                      <p key={line} className="text-white text-sm leading-relaxed">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-white/10 pt-8">
              <div className="flex items-center gap-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/ca93d521-d9a1-49bd-bfc9-30bd65939bc0.png"
                  alt="AKIAN DOO"
                  className="h-10 w-auto object-contain"
                />
                <div>
                  <p className="text-white font-bold text-sm">AKIAN DOO</p>
                  <p className="text-white/40 text-xs">iOS Application Development</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            <h3 className="text-white font-bold text-lg mb-6">Send a Message</h3>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-white/50 text-xs font-medium tracking-wide uppercase block mb-1.5">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00d4aa]/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-medium tracking-wide uppercase block mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00d4aa]/50 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/50 text-xs font-medium tracking-wide uppercase block mb-1.5">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  required
                  placeholder="How can we help?"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00d4aa]/50 transition-colors"
                />
              </div>

              <div>
                <label className="text-white/50 text-xs font-medium tracking-wide uppercase block mb-1.5">
                  Message
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us more..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00d4aa]/50 transition-colors resize-none"
                />
                <p className="text-white/30 text-xs text-right mt-1">{charCount}/500</p>
              </div>

              {status === 'success' && (
                <div className="bg-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-lg px-4 py-3 text-[#00d4aa] text-sm">
                  Message sent successfully! We&apos;ll get back to you soon.
                </div>
              )}
              {status === 'error' && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                  Something went wrong. Please try again or contact us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full bg-[#00d4aa] text-gray-900 font-bold text-sm py-3.5 rounded-lg hover:bg-[#00bfa0] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="mt-20 border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">
            &copy; {new Date().getFullYear()} AKIAN DOO. All rights reserved.
          </p>
          <p className="text-white/30 text-xs">
            Velimira Zivojinovica Masuke 8, Velika Plana 238623, Serbia
          </p>
        </div>
      </div>
    </section>
  );
}
